import {useMessages} from "i18n";
import type {EmailVerificationSendFailure} from "../auth/emailVerificationSend";
import type {Messages} from "../i18n/en";
import {useEmailVerificationPrompt} from "./useEmailVerificationPrompt";
import {Copy, Detail, Message, Root, VerifyButton} from "./VerifyEmailCallout.styles";

/**
 * The message for a failed send. A `Record` keyed by the failure union rather than a switch, so a new
 * outcome in `auth/emailVerificationSend` is a COMPILE error here instead of a silent blank line
 * where a user was owed an explanation.
 */
function failureText(m: Messages, failure: EmailVerificationSendFailure): string {
	const copy: Record<EmailVerificationSendFailure, string> = {
		rateLimited: m.banner.verifyEmail.rateLimited,
		network: m.banner.verifyEmail.network,
		signedOut: m.banner.verifyEmail.signedOut,
		failed: m.banner.verifyEmail.failed,
	};
	return copy[failure];
}

/**
 * The unverified-address callout (ui#1458): shown to a signed-in account whose provider authenticated
 * the person but declined to vouch for the asserted address — every Facebook sign-in, since Facebook
 * hosts no mailboxes, and any provider asserting an address it does not host.
 *
 * PLACEMENT (owner, 2026-08-23): a slim callout above the drop area on the files panel — the surface
 * every user sees — with the verify control inline, plus the same callout in the account sheet so it
 * stays findable later. Explicitly NOT a modal and NOT a takeover of the send button: under
 * kitecorp/transferify-api-service#832 the account is fully functional, so this informs, offers the
 * fix, and gates nothing.
 *
 * BEHAVIOUR: the mail goes out automatically the first time an account is seen, so the common case
 * opens straight in the teal check-your-inbox state with nothing to press. A later session while
 * still unverified shows the blue prompt state with a RESEND control. Both paths surface a failed send —
 * the api's per-account budget above all — as a calm inline line rather than a crash or a silent nothing.
 *
 * Markup + behavior only: visibility, the send, the once-per-account rule and the outcome all come
 * from {@link useEmailVerificationPrompt}, which delegates to React-free modules.
 */
export function VerifyEmailCallout() {
	const m = useMessages();
	const {visible, state, action, sending, failure, alreadyVerified, send} = useEmailVerificationPrompt();

	if (!visible) {
		return null;
	}
	const copy = m.banner.verifyEmail;

	return (
		<Root $tone={state} data-testid="verify-email-callout" data-callout-state={state}>
			<Copy>
				<Message variant="body2">
					{state === "sent" ? copy.sent : copy.message}
				</Message>
				{/*
				  * `role="status"` is on THIS line and not on the Root. On the Root it would make the
				  * whole callout one atomic live region, so every state change re-announces the message
				  * and the control — when the only news is the send's outcome.
				  */}
				{failure !== null && (
					<Detail variant="caption" role="status">{failureText(m, failure)}</Detail>
				)}
				{alreadyVerified && (
					<Detail variant="caption" role="status">{copy.alreadyVerified}</Detail>
				)}
			</Copy>
			{state === "prompt" && (
				<VerifyButton
					size="small"
					disableElevation
					variant="outlined"
					disabled={sending}
					onClick={send}
				>
					{sending
						? copy.sending
						: (action === "resend" ? m.banner.resendEmail : copy.action)}
				</VerifyButton>
			)}
		</Root>
	);
}
